import { required, between, minLength, maxLength, numeric, email, requiredIf } from '@vuelidate/validators';
import {
	validZIPUS,
	validZIPInternational,
	validSSN,
	isSSNUsed,
	validDate,
	validDateOfBirth,
	validDriversLicenseExpirationDate,
	validName,
} from '@core/services/custom-validators';
import { EmployeePermissionsType } from '@core/classes/SharedEnums';
import api from '@core/services/api/_api';
import Application from './Applications/Application';
import ApplicationsBusinessInfo from './Applications/ApplicationsBusinessInfo';
import auth from '@core/services/auth';
import CBCHelper from '@core/helpers/Providers/cbc-helper';
import { CustomerBase } from './_Base/CustomerBase';
import CustomerEmployment from './Customer/CustomerEmployment';
import CustomerHousing from './Customer/CustomerHousing';
import CustomerMetadata from '@core/classes/CustomerMetadata';
import DocumentAssociation from './DocumentAssociation';
import ENUMS from '@core/classes/Enums';
import FIMenuCreditPulls from './FIMenu/FIMenuCreditPulls';
import FIMenuCreditReport from './FIMenuCreditReport';
import FIMenuCustomerIdentification from '@core/classes/FIMenuCustomerIdentification';
import IDVHelper from '@core/helpers/idv-helper';
import LogRocket from 'logrocket';
import moment from 'moment';
import PlaidIDVOverride from './PlaidIDVOverride';
import settings from 'settings';
import StateHelper from '@core/classes/StateHelper';
import util from '@core/services/util';

export default class FIMenuCustomer extends CustomerBase {
	constructor(init) {
		super(init);
		if (init) {
			this.applicationId = init.applicationId;
			this.associatedCustomers = init.associatedCustomers; // List
			this.businessInfo = new ApplicationsBusinessInfo(init.businessInfo);
			this.isEntity = init.isEntity;
			/** @deprecated use customerHousing.currentResidence instead. TODO: Remove after forms are updated. */
			this.address = init.address;
			this.addressExtra = init.addressExtra;
			this.city = init.city;
			this.state = init.state;
			this.zip = init.zip;
			this.county = init.county;
			this.country = init.country;
			/** ------------------------  */
			this.phone = init.phone;
			this.signature = init.signature;
			this.initials = init.initials;
			this.customerApplication = new Application(init.customerApplication);
			this.creditReport = new FIMenuCreditReport(init.creditReport);
			this.validationTypes = FIMenuCustomer.validationTypes;
			this.plaidIDVOverride = init.plaidIDVOverride ?? null;
			this.idvOverride = init.idvOverride?.map(x => new PlaidIDVOverride(x)) ?? [];
			this.isThereAnyUnsavedCustomerChanges = !!init.isThereAnyUnsavedCustomerChanges;
			this.statusIDV = init.statusIDV;
			this.isIDVSkipped = init.isIDVSkipped;
			this.customerApplicationStatus = init.customerApplicationStatus;
			this.metadata = new CustomerMetadata(init.metadata);
			this.currentSignatureId = init.currentSignatureId ?? null;
			this.currentInitialsId = init.currentInitialsId ?? null;

			if (init.dob) {
				this.dob = moment(init.dob, ['YYYY-MM-DDTHH:mm:ss', 'MM/DD/YYYY']).format('MM/DD/YYYY');
			} else {
				this.dob = null;
			}

			this.creditScore = init.creditScore;
			this.isSalesTaxExempt = init.isSalesTaxExempt;

			// credit pulls
			if (init.creditPulls && Array.isArray(init.creditPulls)) {
				this.creditPulls = init.creditPulls.map(a => new FIMenuCreditPulls(a));
			} else {
				this.creditPulls = [];
			}

			//clean up
			this.updateFullName();
		} else {
			this.applicationId = null;
			this.associatedCustomers = [];
			this.businessInfo = null;
			this.isEntity = false;
			this.address = null;
			this.addressExtra = null;
			this.city = null;
			this.state = null;
			this.zip = null;
			this.county = null; //simcha added
			this.country = null; //Gretel added
			this.phone = null;
			this.signature = null;
			this.initials = null;
			this.currentSignatureId = null;
			this.currentInitialsId = null;
			this.cell = null;
			this.workPhone = null;
			this.creditReport = new FIMenuCreditReport();
			this.creditPulls = [];
			this.validationTypes = null;
			this.creditScore = null;
			this.isSalesTaxExempt = null;
			this.plaidIDVOverride = null;
			this.idvOverride = [];
			this.isThereAnyUnsavedCustomerChanges = false;
			this.customerApplication = null;
			this.statusIDV = null;
			this.isIDVSkipped = false;
			this.customerApplicationStatus = null;
			this.metadata = new CustomerMetadata();
		}

		//ADD DRIVERS LICENSE IF ITS MISSING
		if (!this.identifications.some(i => i.type == ENUMS.CUSTOMER_IDENTIFICATION_TYPES.DriversLicense)) {
			let driverLicenseIdentity = new FIMenuCustomerIdentification();
			driverLicenseIdentity.type = ENUMS.CUSTOMER_IDENTIFICATION_TYPES.DriversLicense;
			this.identifications.push(driverLicenseIdentity);
		}

		this.usingAddressExtra = this.address || this.zip || this.city || this.state || this.county ? !!this.addressExtra : null;
		this.usingUSAddress = this.country != null ? this.country == 'US' : null;
	}

	getSanitizedData() {
		let sanitizedData = new FIMenuCustomer(this);
		sanitizedData.customerApplication = null;
		sanitizedData.creditReport = null;
		sanitizedData.creditPulls = null;

		return sanitizedData;
	}

	static validationTypes = {
		JUST_NAME: 'JUST_NAME',
		FINAL: 'FINAL',
		DEFAULT: 'DEFAULT',
		COCUSTOMER: 'COCUSTOMER',
		TRADEINOWNER: 'TRADEINOWNER',
		CUSTOMER_CREATION: 'CUSTOMER_CREATION',
	};

	getIdentification(type) {
		return this.identifications.find(i => i.type == type);
	}

	updateFullName() {
		if (!this.isEntity && this.firstName && this.lastName) {
			this.fullName = this.firstName.concat(' ').concat(this.lastName);
		}
	}

	//Takes CustomerClass
	setFromCustomer(customer) {
		this.id = customer.id || null;
		this.dateCreated = customer.dateCreated;
		this.suffix = customer.generationSuffix;
		this.fullName = customer.fullName;
		this.firstName = customer.firstName;
		this.lastName = customer.lastName;
		this.middleName = customer.middleName;
		this.isEntity = customer.isEntity || !customer.dateOfBirth;
		this.plaidIDVOverride = customer.plaidIDVOverride;
		this.idvOverride = customer.idvOverride;
		this.statusIDV = customer.statusIDV;
		this.isIDVSkipped = customer.isIDVSkipped;
		this.language = customer.language;
		this.customerApplicationStatus = customer.customerApplicationStatus;
		this.businessInfo = customer.businessInfo;

		// Parse addresses
		if (customer.addresses && customer.addresses.length > 0) {
			const firstAddress = customer.addresses[customer.addresses.length - 1];

			this.usingAddressExtra = !!firstAddress.addressLine2;
			this.usingUSAddress = firstAddress.country == 'US';
			this.address = firstAddress.addressLine1 || null;
			this.addressExtra = firstAddress.addressLine2 || null;
			this.city = firstAddress.city || null;
			this.state = firstAddress.state || null;
			this.zip = firstAddress.zipCode || null;
			this.county = firstAddress.county || null;
			this.country = firstAddress.country || null;
			this.phone = firstAddress.homePhoneNumber || null;
		} else {
			this.usingAddressExtra = null;
			this.usingUSAddress = null;
			this.address = null;
			this.addressExtra = null;
			this.city = null;
			this.state = null;
			this.zip = null;
			this.county = null;
			this.country = null;
			this.phone = null;
		}
		this.prefix = customer.prefix;

		this.email = customer.emailAddress;
		this.cell = customer.phoneNumber;
		this.creditReport = new FIMenuCreditReport(customer.creditReport); // You may need to adjust this based on the structure of your FIMenuCreditReport class
		this.creditPulls = customer?.creditPulls?.map(cp => new FIMenuCreditPulls(cp)) ?? [];
		this.ssnLast4 = customer.ssnLast4;
		this.ssnHashed = customer.ssnHashed;
		this.identifications = (customer.identifications || []).map(identification => new FIMenuCustomerIdentification(identification));
		this.customerApplication = customer.customerApplication;
		this.metadata = new CustomerMetadata(customer.metadata);

		this.employmentInformation = customer.employmentInformation ? new CustomerEmployment(customer.employmentInformation) : null;
		this.housingInformation = customer.housingInformation ? new CustomerHousing(customer.housingInformation) : null;

		if (!this.identifications.some(i => i.type == ENUMS.CUSTOMER_IDENTIFICATION_TYPES.DriversLicense)) {
			let driverLicenseIdentity = new FIMenuCustomerIdentification();
			driverLicenseIdentity.type = ENUMS.CUSTOMER_IDENTIFICATION_TYPES.DriversLicense;
			this.identifications.push(driverLicenseIdentity);
		}

		if (customer.dateOfBirth) {
			this.dob = moment(customer.dateOfBirth, ['YYYY-MM-DDTHH:mm:ss', 'MM/DD/YYYY']).format('MM/DD/YYYY');
		}

		this.cdkCustomerInfo = customer.cdkCustomerInfo;
	}

	//Takes FIMenuCustomer
	setFromFIMenuCustomer(fimenuCustomer, storeCode = null) {
		this.id = fimenuCustomer.id || null;
		this.suffix = fimenuCustomer.suffix;
		this.fullName = fimenuCustomer.fullName;
		this.firstName = fimenuCustomer.firstName;
		this.lastName = fimenuCustomer.lastName;
		this.middleName = fimenuCustomer.middleName;
		this.isEntity = fimenuCustomer.isEntity || !!fimenuCustomer.dob;
		this.usingUSAddress = fimenuCustomer.country == 'US';
		this.usingAddressExtra = !!fimenuCustomer.addressExtra;
		this.address = fimenuCustomer.address;
		this.addressExtra = fimenuCustomer.addressExtra;
		this.city = fimenuCustomer.city;
		this.state = fimenuCustomer.state;
		this.zip = fimenuCustomer.zip;
		this.county = fimenuCustomer.county;
		this.country = fimenuCustomer.country;

		this.prefix = fimenuCustomer.prefix;
		this.statusIDV = fimenuCustomer.statusIDV;
		this.isIDVSkipped = fimenuCustomer.isIDVSkipped;
		this.language = fimenuCustomer.language;
		this.customerApplicationStatus = fimenuCustomer.customerApplicationStatus;
		this.metadata = new CustomerMetadata(fimenuCustomer.metadata);

		this.email = fimenuCustomer.email;
		this.cell = fimenuCustomer.cell;
		this.creditReport = new FIMenuCreditReport(fimenuCustomer.creditReport); // You may need to adjust this based on the structure of your FIMenuCreditReport class
		this.creditPulls = fimenuCustomer?.creditPulls?.map(cp => new FIMenuCreditPulls(cp)) ?? [];
		this.ssnLast4 = fimenuCustomer.ssnLast4;
		this.ssnHashed = fimenuCustomer.ssnHashed;
		this.identifications = (fimenuCustomer.identifications || []).map(identification => new FIMenuCustomerIdentification(identification));
		this.customerApplication = fimenuCustomer.customerApplication;
		this.plaidIDVOverride = fimenuCustomer.plaidIDVOverride || null;
		this.idvOverride = fimenuCustomer.idvOverride;

		if (!this.identifications.some(i => i.type == ENUMS.CUSTOMER_IDENTIFICATION_TYPES.DriversLicense)) {
			let driverLicenseIdentity = new FIMenuCustomerIdentification();
			driverLicenseIdentity.type = ENUMS.CUSTOMER_IDENTIFICATION_TYPES.DriversLicense;
			this.identifications.push(driverLicenseIdentity);
		}

		if (fimenuCustomer.dob) {
			this.dob = moment(fimenuCustomer.dob, ['YYYY-MM-DDTHH:mm:ss', 'MM/DD/YYYY']).format('MM/DD/YYYY');
		}
		this.cdkCustomerInfo = fimenuCustomer.cdkCustomerInfo;
	}

	getFullName() {
		if (this.isEntity) {
			return this.fullName;
		} else if (this.firstName && this.lastName) {
			return this.firstName + ' ' + this.lastName;
		} else {
			return null;
		}
	}

	getFullAddress() {
		let fullAddress = '';

		// Concatenate address and addressExtra
		if (this.address) {
			fullAddress = this.address;

			if (this.addressExtra) {
				fullAddress += ', ' + this.addressExtra;
			}
		} else if (this.addressExtra) {
			fullAddress = this.addressExtra;
		}

		// Add city, county, and zip code
		if (this.city) {
			fullAddress += (fullAddress ? ', ' : '') + this.city;
		}

		if (this.state) {
			fullAddress += (fullAddress ? ', ' : '') + this.state;
		}

		// if (this.county) {
		//     fullAddress += (fullAddress ? ", " : "") + this.county;
		// }

		if (this.zip) {
			fullAddress += (fullAddress ? ', ' : '') + this.zip;
		}

		return fullAddress;
	}

	getAndSetCustomerApplication() {
		return api.applications
			.getByCustomer(this.id)
			.then(response => {
				//console.log(response)
				if (response.data) {
					this.customerApplication = new Application(response.data);
					this.applicationId = this.customerApplication.id;
				}
				return response;
			})
			.catch(error => {
				console.error('Error loading customer application', error);
			});
	}

	setCustomerApplication(customerId) {
		//console.log("ID###", this.id)
		return api.applications
			.getByCustomer(customerId)
			.then(response => {
				if (response.data) {
					this.customerApplication = new Application(response.data);
					this.applicationId = response.data.id;
				}
				return response;
			})
			.catch(error => {
				console.error('Error loading customer application', error);
			});
	}

	getAndSetCustomerApplicationByApplicationId(customerApplicationId, retriveIncomplete = false) {
		return api.applications
			.getApplication(customerApplicationId)
			.then(response => {
				if (response.data) {
					this.customerApplication = new Application(response.data);
				}
				return response;
			})
			.catch(error => {
				console.error('Error loading customer application', error);
			});
	}

	getAssociations() {
		let associations = [];

		if (this.id)
			associations.push(
				new DocumentAssociation({
					type: ENUMS.DOC_ASSOCIATION.Customer,
					id: this.id,
				}),
			);
		if (this.applicationId)
			associations.push(
				new DocumentAssociation({
					type: ENUMS.DOC_ASSOCIATION.CustomerApplication,
					id: this.applicationId,
				}),
			);

		return associations;
	}

	populateWithCustomerApplication(populateAsBusiness) {
		//this.isEntity = this.customerApplication.quoteAppData.applicationsDataAnswered.isBusiness
		this.language = this.customerApplication.language;

		if (populateAsBusiness) {
			this.isEntity = true;
			this.fullName = this.customerApplication.quoteAppData.applicationsDataAnswered.businessInfo.legalName;
			this.firstName = null;
			this.middleName = null;
			this.lastName = null;
			this.suffix = null;

			this.dob = this.customerApplication.quoteAppData.applicationsDataAnswered?.personalInfo?.dateOfBirth;
			this.email = this.customerApplication.quoteAppData.applicationsDataAnswered.businessInfo.emailAddress;
			this.cell = this.customerApplication.quoteAppData.applicationsDataAnswered.businessInfo.phoneNumber;
			this.phone = null;

			let address = this.customerApplication.quoteAppData.applicationsDataAnswered.businessInfo.address;
			this.usingUSAddress = this.country != null ? this.country == 'US' : null;
			this.usingAddressExtra = !!address.addressLine2;
			this.zip = address.zipCode;
			this.address = address.addressLine1;
			this.addressExtra = address.addressLine2;
			this.city = address.city;
			this.state = address.state;
			this.county = address.county;
			this.country = address.country;

			this.ssnHashed = null;
			this.ssnLast4 = null;
			this.creditScore = null;
		} else {
			//console.log("ENTERED populateWithCustomerApplication-nonBUSINESS");
			this.isEntity = false;

			let personalInfo = this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo;
			this.prefix = personalInfo.prefix;
			this.firstName = personalInfo.firstName;
			this.middleName = personalInfo.middleName;
			this.lastName = personalInfo.lastName;
			this.suffix = personalInfo.generationSuffix;

			this.dob = moment(personalInfo.dateOfBirth, 'YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]').format('MM/DD/YYYY');
			this.email = personalInfo.emailAddress;
			this.cell = personalInfo.phoneNumber;
			this.phone = personalInfo.currentResidence.homePhoneNumber;

			let address = personalInfo.currentResidence;
			this.usingUSAddress = this.country != null ? this.country == 'US' : null;
			this.usingAddressExtra = !!address.addressLine2;
			this.zip = address.zipCode;
			this.address = address.addressLine1;
			this.addressExtra = address.addressLine2;
			this.city = address.city;
			this.state = address.state;
			this.county = address.county;
			this.country = address.country;

			let customerAppDriverLicense = personalInfo.identifications?.find(i => i.type == ENUMS.CUSTOMER_IDENTIFICATION_TYPES.DriversLicense);

			if (customerAppDriverLicense) {
				let driverLicense = new FIMenuCustomerIdentification(customerAppDriverLicense);

				let index = this.identifications?.findIndex(i => i.type == ENUMS.CUSTOMER_IDENTIFICATION_TYPES.DriversLicense);

				if (driverLicense) {
					if (index >= 0) {
						this.identifications[index] = driverLicense;
					} else {
						this.identifications.push(driverLicense);
					}
				}
			}

			this.ssnHashed = this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.ssnHashed;
			this.ssnLast4 = this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.ssnLast4;
			this.creditScore = null;
		}

		this.customerApplicationStatus = this.customerApplication.customerApplicationStatus;
	}

	populateCustomerApplicationWithCustomer(populateAsBusiness) {
		//this.isEntity = this.customerApplication.quoteAppData.applicationsDataAnswered.isBusiness

		this.customerApplication.id = this.customerApplication.id ? this.customerApplication.id : util.getGuid();
		this.customerApplication.leadId = this.customerApplication.leadId ? this.customerApplication.leadId : util.getGuid();
		this.customerApplication.customerId = this.id ?? util.getGuid();
		this.customerApplication.dateCreated = this.customerApplication.dateCreated ? this.customerApplication.dateCreated : moment().utc();
		this.customerApplication.editLinkExpiration = this.customerApplication.editLinkExpiration
			? moment(this.customerApplication.editLinkExpiration, 'YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]')
			: moment().utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]');
		this.customerApplication.emailCodeExpiration = this.customerApplication.emailCodeExpiration
			? moment(this.customerApplication.emailCodeExpiration, 'YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]')
			: moment().utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]');
		this.customerApplication.phoneCodeExpiration = this.customerApplication.phoneCodeExpiration
			? moment(this.customerApplication.phoneCodeExpiration, 'YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]')
			: moment().utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]');

		if (populateAsBusiness) {
			this.customerApplication.quoteAppData.applicationsDataAnswered.isBusiness = this.isEntity ?? populateAsBusiness;
			this.customerApplication.quoteAppData.applicationsDataAnswered.businessInfo.legalName = this.fullName;

			// Applicant information.
			this.customerApplication.preFilledData.dateOfBirth = this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.dateOfBirth ?? this.dob;
			this.customerApplication.preFilledData.emailAddress = this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.emailAddress ?? this.email;
			this.customerApplication.preFilledData.firstName = this.firstName;
			this.customerApplication.preFilledData.lastName = this.lastName;
			this.customerApplication.preFilledData.phoneNumber = this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.phoneNumber ?? this.cell;

			this.customerApplication.quoteAppData.applicationsDataAnswered.businessInfo.emailAddress = this.email;
			this.customerApplication.quoteAppData.applicationsDataAnswered.businessInfo.phoneNumber = this.cell;
			this.customerApplication.quoteAppData.applicationsDataAnswered.businessInfo.address.addressLine2 = this.addressExtra;
			this.customerApplication.quoteAppData.applicationsDataAnswered.businessInfo.address.addressLine1 = this.address;
			this.customerApplication.quoteAppData.applicationsDataAnswered.businessInfo.address.zipCode = this.zip;
			this.customerApplication.quoteAppData.applicationsDataAnswered.businessInfo.address.city = this.city;
			this.customerApplication.quoteAppData.applicationsDataAnswered.businessInfo.address.state = this.state;
			this.customerApplication.quoteAppData.applicationsDataAnswered.businessInfo.address.county = this.county;
			this.customerApplication.quoteAppData.applicationsDataAnswered.businessInfo.address.country = this.country;
		} else {
			this.customerApplication.quoteAppData.applicationsDataAnswered.isBusiness = this.isEntity;
			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.prefix = this.prefix;
			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.firstName = this.firstName;
			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.middleName = this.middleName;
			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.lastName = this.lastName;
			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.generationSuffix = this.suffix;

			this.customerApplication.preFilledData.dateOfBirth = this.dob;
			this.customerApplication.preFilledData.emailAddress = this.email;
			this.customerApplication.preFilledData.firstName = this.firstName;
			this.customerApplication.preFilledData.lastName = this.lastName;
			this.customerApplication.preFilledData.phoneNumber = this.phone;

			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.dateOfBirth = this.dob;
			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.emailAddress = this.email;
			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.phoneNumber = this.cell;
			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.currentResidence.homePhoneNumber = this.phone;
			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.currentResidence.zipCode = this.zip;

			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.currentResidence.addressLine1 = this.address;
			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.currentResidence.addressLine2 = this.addressExtra;
			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.currentResidence.city = this.city;
			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.currentResidence.state = this.state;
			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.currentResidence.county = this.county;
			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.currentResidence.country = this.country;

			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.identifications = this.identifications;
			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.ssnHashed = this.ssnHashed;

			this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.ssnLast4 = this.ssnLast4;
		}
	}

	populateWithApplicant(applicantInfo) {
		this.firstName = applicantInfo.firstName;
		this.lastName = applicantInfo.lastName;
		this.middleName = applicantInfo.middleName;
		this.email = applicantInfo.emailAddress;
		this.ssnLast4 = applicantInfo.ssnLast4;
		this.usingUSAddress = applicantInfo.currentResidence.country != null ? applicantInfo.currentResidence.country == 'US' : null;
		this.usingAddressExtra = applicantInfo.currentResidence.addressLine2 != null ? true : false;
		this.address = applicantInfo.currentResidence.addressLine1;
		this.addressExtra = applicantInfo.currentResidence.addressLine2;
		this.city = applicantInfo.currentResidence.city;
		this.county = applicantInfo.currentResidence.county;
		this.zip = applicantInfo.currentResidence.zipCode;
		this.state = applicantInfo.currentResidence.state;
		this.country = applicantInfo.currentResidence.country;
		this.dob = applicantInfo.dateOfBirth;
		this.suffix = applicantInfo.generationSuffix;
		this.prefix = applicantInfo.prefix;
		this.cell = applicantInfo.phoneNumber;
		this.phone = applicantInfo.currentResidence.homePhoneNumber;
	}

	isITIN() {
		if (this.customerApplication && this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.ssnHashed) {
			return this.customerApplication.quoteAppData.applicationsDataAnswered.personalInfo.ssnType == ENUMS.SSN_TYPES.ITIN;
		}
		return false;
	}

	validation(fimenu, type = FIMenuCustomer.validationTypes.DEFAULT) {
		//const customerApplicationDisabled = fimenu?.customerApplication == ENUMS.CUSTOMER_RETRIEVAL_STATUS.RetrieveApplicationEnabled
		const customerHasAccessToFIManager = auth.getTokenPayload().hasAccessToPermission(EmployeePermissionsType.AccessFIMenu);

		let driversLicenseValidation = identification => {
			return {
				idNumber: {
					validDLFormat: (value, i) => {
						if (i.type !== ENUMS.CUSTOMER_IDENTIFICATION_TYPES.DriversLicense || !value) return true;

						const dlRegex = StateHelper.getDriversLicenseRegex(i.jurisdiction);
						if (dlRegex == null) return true;

						return dlRegex.test(value);
					},
				},
				expirationDate: {
					//required: requiredIf(() => !this.isEntity && identification.type == ENUMS.CUSTOMER_IDENTIFICATION_TYPES.DriversLicense && !!fimenu?.buyersOrderEnabled && customerHasAccessToFIManager),
					validDate: (value, i) => {
						if (
							!this.isEntity &&
							i.type == ENUMS.CUSTOMER_IDENTIFICATION_TYPES.DriversLicense &&
							!!fimenu?.buyersOrderEnabled &&
							customerHasAccessToFIManager
						)
							return validDate(value);
						else return true;
					},
					validDriversLicenseExpirationDate: (value, i) => {
						if (
							!this.isEntity &&
							i.type == ENUMS.CUSTOMER_IDENTIFICATION_TYPES.DriversLicense &&
							!!fimenu?.buyersOrderEnabled &&
							customerHasAccessToFIManager
						)
							return validDriversLicenseExpirationDate(value);
						else return true;
					},
				},
				jurisdiction: {
					//required: requiredIf(!this.isEntity && identification.type == ENUMS.CUSTOMER_IDENTIFICATION_TYPES.DriversLicense && !!fimenu?.buyersOrderEnabled && customerHasAccessToFIManager)
				},
			};
		};

		let data = {
			number: {},
			isEntity: {},
			prefix: {},
			fullName: {},
			firstName: {},
			middleName: {},
			lastName: {},
			suffix: {},
			address: {},
			addressExtra: {},
			city: {},
			state: {},
			zip: {},
			phone: {},
			email: {},
			signature: {},
			cell: {},
			workPhone: {},
			county: {},
			country: {},
			dob: {},
			creditScore: { between: between(300, 850) },
			ssnLast4: {},
			ssnHashed: {},
			isCitizen: {},
			isSalesTaxExempt: {},
			creditReport: {
				flags: {},
			},
			customerApplication: {
				quoteAppData: {
					applicationsDataAnswered: {
						businessInfo: {
							tradeName: {},
							enterpriseType: {},
							businessStartDate: {},
							grossMontlyProfit: {},
							businessOperator: {
								operatorSameAsApplicant: {},
								firstName: {},
								lastName: {},
								relationshipDescription: {},
								address: {},
							},
							businessPrincipal: {
								principalSameAsApplicant: {},
								firstName: {},
								lastName: {},
								jobTitle: {},
								address: {},
								phoneNumber: {},
								dateOfBirth: {},
								percentageOfOwnership: {},
							},
						},
					},
				},
				id: {},
				status: {},
			},
		};

		const applicationsDataAnswered = this.customerApplication?.quoteAppData.applicationsDataAnswered;

		const requiredIfMeetsResubmitCondition = redFlag => {
			let exists = redFlag.commentAlreadyExists == true;
			let empty = redFlag.comment == null;
			return exists == true && empty == true;
		};

		const minWordsToPass = (value, targetWords) => {
			const words = value.trim().split(' ');
			return words.length >= targetWords;
		};

		switch (type) {
			case FIMenuCustomer.validationTypes.DEFAULT:
				data.fullName = {
					required: requiredIf(() => this.isEntity),
					maxLengthLength: this.isEntity ? maxLength(60) : maxLength(120),
				};
				data.prefix = {
					required: requiredIf(() => !this.isEntity),
				};
				data.firstName = {
					required: requiredIf(() => !this.isEntity),
					valid: validName,
				};
				data.middleName = {
					valid: validName,
				};
				data.lastName = {
					required: requiredIf(() => !this.isEntity),
					valid: validName,
				};
				data.dob = {
					required: requiredIf(() => !this.isEntity),
					validDateOfBirth,
				};
				data.isCitizen = {
					//temporarily remove validation
					/*required: requiredIf(c => !c.isEntity),*/
				};
				data.ssnLast4 = {
					validSSN: (value, customer) => {
						if (value && customer.ssnHashed !== null) return true;

						if (customer.ssnHashed === null) return validSSN(value);
						else return true;
					},
				};
				data.ssnHashed = {
					// isSSNUsed: (ssnHashed, c) => {
					//     if (this.isEntity) {
					//         return true;
					//     }
					//     return isSSNUsed(ssnHashed, fimenu);
					// }
				};

				data.address = { required };
				data.addressExtra = {
					required: requiredIf(() => this.usingAddressExtra),
				};
				data.city = { required };
				data.state = {
					required,
					minLength: this.usingUSAddress == false ? {} : minLength(2),
					maxLength: this.usingUSAddress == false ? {} : maxLength(2),
				};
				data.county = { required: requiredIf(() => this.usingUSAddress) };
				data.country = { required };
				data.zip = {
					required,
					validZIPUS: () => {
						if (this.usingUSAddress != false) {
							return validZIPUS(this.zip);
						}
						return true;
					},
					validZIPInternational: () => {
						if (this.usingUSAddress == false) {
							return validZIPInternational(this.zip);
						}
						return true;
					},
				};
				data.email = { required, email };
				data.phone = { minLength: minLength(10), maxLength: maxLength(10) };
				data.cell = {
					required,
					minLength: minLength(10),
					maxLength: maxLength(10),
				};
				data.workPhone = { minLength: minLength(10), maxLength: maxLength(10) };
				data.identifications = this.identifications.map(identification => driversLicenseValidation(identification));
				//data.isSalesTaxExempt = { required: requiredIf(() => this.isEntity) };
				data.id = { required };
				data.isThereAnyUnsavedCustomerChanges = {
					isValid: () => !this.isThereAnyUnsavedCustomerChanges,
				};
				data.creditReport.flags = this.creditReport.flags.map(flag => ({
					redflags: flag.redflags.map(redFlag => ({
						comment: {
							required,
							minLength: requiredIf(() => redFlag.comment) && minLength(11),
							minWords: () => (redFlag.comment ? minWordsToPass(redFlag.comment, 3) : true),
						},
					})),
				}));

				data.statusIDV = {
					required: requiredIf(() => !this.isEntity && IDVHelper.isIDVEnabled(fimenu?.store) && !this.isIDVSkipped),
					isValid: () => {
						return (
							this.isEntity || !IDVHelper.isIDVEnabled(fimenu?.store) || this.isIDVSkipped || this.statusIDV === ENUMS.PLAID_VERIFICATION_STATUS.Success.ind
						);
					},
				};

				data.customerApplication.id = {
					required: requiredIf(() => !this.isEntity && fimenu.customerApplication === ENUMS.CUSTOMER_RETRIEVAL_STATUS.RetrieveApplicationEnabled),
				};
				data.customerApplication.status = {
					required: requiredIf(() => !this.isEntity && fimenu.customerApplication === ENUMS.CUSTOMER_RETRIEVAL_STATUS.RetrieveApplicationEnabled),
					isValid: currentStatus => {
						if (this.isEntity) return true;
						const isCustomerApplicationEnabled = fimenu.customerApplication === ENUMS.CUSTOMER_RETRIEVAL_STATUS.RetrieveApplicationEnabled;
						if (!isCustomerApplicationEnabled) return true;

						const custAppStatuses = ENUMS.CUSTOMER_APPLICATION_STATUS;
						const completed = currentStatus == custAppStatuses.Completed || currentStatus == custAppStatuses.CompletedNoCredit;

						return completed;
					},
				};
				// dateCompleted: {
				//     required: requiredIf(() => fimenu.customerApplication === ENUMS.CUSTOMER_RETRIEVAL_STATUS.RetrieveApplicationEnabled),
				//     isValid: (dateCompleted) => {

				//         const isCustomerApplicationEnabled = fimenu.customerApplication === ENUMS.CUSTOMER_RETRIEVAL_STATUS.RetrieveApplicationEnabled;
				//         if (!isCustomerApplicationEnabled) return true;

				//         if (dateCompleted) {
				//             const daysCount = moment().diff(dateCompleted, 'days')
				//             return daysCount >= settings.lookups.customerApplicationExpiration == true ? false : true
				//         }
				//         return true
				//     }
				// }
				break;

			case FIMenuCustomer.validationTypes.COCUSTOMER:
				if (!fimenu.hasCoSigner) break;

				data.fullName = {
					required: requiredIf(() => this.isEntity),
					maxLengthLength: this.isEntity ? maxLength(60) : maxLength(120),
				};
				data.prefix = { required: requiredIf(() => !this.isEntity) };
				data.isCitizen = {
					//temoprarily remove validation
					//required: requiredIf(() => this.isEntity)
				};
				data.firstName = {
					maxLengthLength: maxLength(60),
					required: requiredIf(() => !this.isEntity),
					valid: validName,
				};
				data.middleName = {
					maxLengthLength: maxLength(60),
					valid: validName,
				};
				data.lastName = {
					maxLengthLength: maxLength(60),
					required: requiredIf(() => !this.isEntity),
					valid: validName,
				};
				data.dob = {
					required: requiredIf(() => !this.isEntity),
					validDateOfBirth,
				};
				data.ssnLast4 = {
					validSSN: (value, customer) => {
						if (value && customer.ssnHashed !== null) return true;

						if (customer.ssnHashed === null) return validSSN(value);
						else return true;
					},
				};
				data.ssnHashed = {
					// isSSNUsed: (ssnHashed, c) => {
					//     if (c.isEntity) {
					//         return true;
					//     }
					//     return isSSNUsed(ssnHashed, fimenu);
					// }
				};

				data.address = { required };
				data.addressExtra = { required: requiredIf(this.usingAddressExtra) };
				data.city = { required };
				data.state = {
					required,
					minLength: this.usingUSAddress == false ? {} : minLength(2),
					maxLength: this.usingUSAddress == false ? {} : maxLength(2),
				};
				data.county = { required: requiredIf(() => this.usingUSAddress) };
				data.zip = {
					required,
					validZIPUS: () => {
						if (this.usingUSAddress != false) {
							return validZIPUS(this.zip);
						}
						return true;
					},
					validZIPInternational: () => {
						if (this.usingUSAddress == false) {
							return validZIPInternational(this.zip);
						}
						return true;
					},
				};
				data.email = { required, email };
				data.phone = { minLength: minLength(10), maxLength: maxLength(10) };
				data.cell = {
					required,
					minLength: minLength(10),
					maxLength: maxLength(10),
				};
				data.workPhone = { minLength: minLength(10), maxLength: maxLength(10) };
				data.identifications = this.identifications.map(identification => driversLicenseValidation(identification));
				data.isSalesTaxExempt = { required: requiredIf(() => this.isEntity) };
				data.id = { required };
				data.isThereAnyUnsavedCustomerChanges = {
					isValid: () => !this.isThereAnyUnsavedCustomerChanges,
				};

				data.statusIDV = {
					required: requiredIf(() => !this.isEntity && IDVHelper.isIDVEnabled(fimenu?.store) && !this.isIDVSkipped),
					isValid: () => {
						return (
							this.isEntity || !IDVHelper.isIDVEnabled(fimenu?.store) || this.isIDVSkipped || this.statusIDV === ENUMS.PLAID_VERIFICATION_STATUS.Success.ind
						);
					},
				};
				data.creditReport.flags = this.creditReport.flags.map(flag => ({
					redflags: flag.redflags.map(redFlag => ({
						comment: {
							required,
							minLength: requiredIf(() => redFlag.comment) && minLength(11),
							minWords: () => (redFlag.comment ? minWordsToPass(redFlag.comment, 3) : true),
						},
					})),
				}));

				data.customerApplication = {
					id: {
						required: requiredIf(() => fimenu.hasCoSigner && fimenu.customerApplication === ENUMS.CUSTOMER_RETRIEVAL_STATUS.RetrieveApplicationEnabled),
					},
					status: {
						isValid: currentStatus => {
							const isCustomerApplicationEnabled = fimenu.customerApplication === ENUMS.CUSTOMER_RETRIEVAL_STATUS.RetrieveApplicationEnabled;
							if (!isCustomerApplicationEnabled) return true;

							const custAppStatuses = ENUMS.CUSTOMER_APPLICATION_STATUS;
							const completed = currentStatus == custAppStatuses.Completed || currentStatus == custAppStatuses.CompletedNoCredit;

							return completed;
						},
					},
					// dateCompleted: {
					//     required: requiredIf(() => fimenu.customerApplication === ENUMS.CUSTOMER_RETRIEVAL_STATUS.RetrieveApplicationEnabled),
					//     isValid: (dateCompleted) => {

					//         const isCustomerApplicationEnabled = fimenu.customerApplication === ENUMS.CUSTOMER_RETRIEVAL_STATUS.RetrieveApplicationEnabled;
					//         if (!isCustomerApplicationEnabled) return true;

					//         if (dateCompleted) {
					//             const daysCount = moment().diff(dateCompleted, 'days')
					//             return daysCount >= settings.lookups.customerApplicationExpiration == true ? false : true
					//         }
					//         return true
					//     }
					// }
				};
				break;

			case FIMenuCustomer.validationTypes.JUST_NAME:
				data.fullName = { required: requiredIf(() => this.isEntity) };
				data.firstName = { required: requiredIf(() => !this.isEntity) };
				data.lastName = { required: requiredIf(() => !this.isEntity) };
				data.ssnLast4 = {
					validSSN: (value, customer) => {
						if (value && customer.ssnHashed !== null) return true;

						if (customer.ssnHashed === null) return validSSN(value);
						else return true;
					},
				};
				data.ssnHashed = {
					// isSSNUsed: (ssnHashed, c) => {
					//     if (c.isEntity) {
					//         return true;
					//     }
					//     return isSSNUsed(ssnHashed, fimenu);
					// }
				};
				break;

			case FIMenuCustomer.validationTypes.CUSTOMER_CREATION:
				data.prefix = { required: requiredIf(() => !this.isEntity) };
				data.fullName = {
					required: requiredIf(() => this.isEntity),
					maxLengthLength: this.isEntity ? maxLength(60) : maxLength(120),
				};
				data.firstName = {
					required: requiredIf(() => !this.isEntity),
					valid: validName,
				};
				data.middleName = {
					valid: validName,
				};
				data.lastName = {
					required: requiredIf(() => !this.isEntity),
					valid: validName,
				};
				data.dob = {
					required: requiredIf(() => !this.isEntity),
					validDateOfBirth,
				};
				data.email = { required, email };
				data.cell = {
					required,
					numeric,
					minLength: minLength(10),
					maxLength: maxLength(10),
				};
				data.identifications = this.identifications.map(identification => driversLicenseValidation(identification));
				data.ssnLast4 = {
					validSSN: (value, customer) => {
						if (value && customer.ssnHashed !== null) return true;

						if (customer.ssnHashed === null) return validSSN(value);
						else return true;
					},
				};
				data.ssnHashed = {
					required: requiredIf(() => this.ssnLast4),
				};

				data.address = { required };
				data.addressExtra = {
					required: requiredIf(() => this.usingAddressExtra),
				};
				data.city = { required };
				data.state = {
					required,
					minLength: this.usingUSAddress == false ? {} : minLength(2),
					maxLength: this.usingUSAddress == false ? {} : maxLength(2),
				};
				data.county = { required: requiredIf(() => this.usingUSAddress) };
				data.zip = {
					required,
					validZIPUS: () => {
						if (this.usingUSAddress != false) {
							return validZIPUS(this.zip);
						}
						return true;
					},
					validZIPInternational: () => {
						if (this.usingUSAddress == false) {
							return validZIPInternational(this.zip);
						}
						return true;
					},
				};
				data.creditReport.flags = this.creditReport.flags.map(flag => ({
					redflags: flag.redflags.map(redFlag => ({
						comment: {
							required,
							minLength: requiredIf(() => redFlag.comment) && minLength(11),
							minWords: () => (redFlag.comment ? minWordsToPass(redFlag.comment, 3) : true),
						},
					})),
				}));
				break;

			case FIMenuCustomer.validationTypes.FINAL:
				data.address = { required };
				data.city = { required };
				data.county = { required: requiredIf(() => this.usingUSAddress) };
				data.zip = {
					required,
					validZIPUS: () => {
						if (this.usingUSAddress != false) {
							return validZIPUS(this.zip);
						}
						return true;
					},
					validZIPInternational: () => {
						if (this.usingUSAddress == false) {
							return validZIPInternational(this.zip);
						}
						return true;
					},
				};
				data.state = {
					required,
					minLength: this.usingUSAddress == false ? {} : minLength(2),
					maxLength: this.usingUSAddress == false ? {} : maxLength(2),
				};
				data.email = { required, email };
				data.phone = {
					numeric,
					minLength: minLength(10),
					maxLength: maxLength(10),
				};
				data.cell = {
					required,
					numeric,
					minLength: minLength(10),
					maxLength: maxLength(10),
				};
				data.address = { required };
				data.addressExtra = {
					required: requiredIf(() => this.usingAddressExtra),
				};
				break;

			case FIMenuCustomer.validationTypes.TRADEINOWNER:
				data.id = { required };
				// data.fullName = { required: requiredIf(() => this.isEntity) };
				// data.prefix = { required: requiredIf(() => !this.isEntity) };
				// data.isCitizen = {
				//     //temporarily remove validation
				//     //required: requiredIf(() => this.isEntity)
				// };
				// data.firstName = { required: requiredIf(() => !this.isEntity) };
				data.middleName = { valid: validName };
				// data.lastName = { required: requiredIf(() => !this.isEntity) };
				// data.dob = {
				//     required: requiredIf(() => !this.isEntity) ,
				//     validDateOfBirth
				// };
				// data.ssnLast4 = {
				//     validSSN: (value, customer) => {

				//         if (value && customer.ssnHashed !== null) return true;

				//         if (customer.ssnHashed === null)
				//             return validSSN(value);
				//         else
				//             return true;
				//     }
				// };
				// data.ssnHashed = { required: requiredIf( () => !this.isEntity && fimenu.dealType !== "Cash") }
				// data.address = { required }
				// data.addressExtra = { required: requiredIf(() => this.usingAddressExtra) }
				// data.city = { required }
				//data.state = {
				//    required,
				//    minLength: this.usingUSAddress != false ?? minLength(2),
				//    maxLength: this.usingUSAddress != false ?? maxLength(2)
				//}
				// data.county = { required: requiredIf(() => this.usingUSAddress) }
				//data.zip = {
				//    required,
				//    validZIPUS: () => {
				//        if (this.usingUSAddress != false) {
				//            return validZIPUS(this.zip);
				//        }
				//        return true;
				//    },
				//    validZIPInternational: () => {
				//        if (this.usingUSAddress == false) {
				//            return validZIPInternational(this.zip);
				//        }
				//        return true;
				//}
				//}
				// data.email = { required, email }
				// data.phone = { minLength: minLength(10), maxLength: maxLength(10) }
				// data.cell = { required, minLength: minLength(10), maxLength: maxLength(10) }
				// data.workPhone = { minLength: minLength(10), maxLength: maxLength(10) }
				break;
		}

		return data;
	}

	/**
	 * @returns if there is an error from CBC api regarding this customer, return true
	 */
	hasCBCError() {
		return this.creditReport && this.creditReport?.response?.error == 'True';
	}

	/**
	 * @returns CBC error message if true, else return false
	 */
	cbcErrorResponse() {
		const thereIsAnErrorResponse = this?.creditReport?.response?.erroR_DESCRIPT;
		const theErrorMessageIsEmpty = this?.creditReport?.response?.erroR_DESCRIPT == '';
		if (thereIsAnErrorResponse && !theErrorMessageIsEmpty) {
			return this.creditReport.response.erroR_DESCRIPT;
		} else return false;
	}

	getCustomerAssociation() {
		new DocumentAssociation({
			id: this.id,
			type: ENUMS.DOC_ASSOCIATION.Customer,
		});
	}

	/**
	 * Gets the newest IDV Session ID found in our ElasticSearch index.
	 * @return {string} The newest IDV Session ID, if found.
	 * @memberof FIMenuCustomer
	 */
	async getIDVSessionID() {
		try {
			const response = await api.plaid.getMostRecentIDVSessionID(this.id, ENUMS.DOC_ASSOCIATION.Customer);
			return response?.data;
		} catch (err) {
			console.error(err);
			return null;
		}
	}

	/**
	 * Gets the IDV session Information from Plaid.
	 * @param {boolean} [updateCustomer=false] Whether we should update the customer
	 * @param {*} [sessionId=null] The session ID of the IDV Session. If nothing is passed in, defaults to the most recent session.
	 * @return {Object} The IDV session data.
	 * @memberof FIMenuCustomer
	 */
	async getIDVSession(updateCustomer = false, sessionId = null) {
		try {
			// If no session ID is specified, try to grab the latest session ID.
			if (!sessionId) {
				sessionId = await this.getIDVSessionID(this.id);
				if (!sessionId) return null;
			}

			let response = await api.plaid.getIdentityVerification(sessionId);
			if (!response?.data)
				throw new Error('Error Fetching IDV Session Data', {
					cause: 'NO RESONSE FROM SERVER',
				});

			const plaidResponse = response.data.plaidResponse;
			if (updateCustomer && plaidResponse) {
				// If we get a sucessful session, the session is no longer considered "skipped"
				if (plaidResponse.status === ENUMS.PLAID_VERIFICATION_STATUS.Success.str) this.isIDVSkipped = false;

				this.statusIDV = ENUMS.PLAID_VERIFICATION_STATUS.stringToIndex(plaidResponse.status);
			}

			return response.data;
		} catch (err) {
			console.error(err);
			return null;
		}
	}

	/**
	 * Gets the most recent IDV session and only returns the current status.
	 * @param {boolean} [updateCustomer=false] Whether to update the customer record with the new IDV information.
	 * @return {string} The status of the newest IDV session as a string enum.
	 * @memberof FIMenuCustomer
	 */
	async getIDVStatus(updateCustomer = false) {
		const idvSession = await this.getIDVSession(updateCustomer);
		return idvSession?.plaidResponse?.status;
	}

	/**
	 * Fetches the current IDV status and updates this.statusIDV with the new value.
	 * @return {boolean} Whether the IDVStatus has been successfully updated.
	 * @memberof FIMenuCustomer
	 */
	async refreshIDVStatus() {
		const sessionStatus = await this.getIDVStatus();
		if (!sessionStatus?.plaidResponse) return false;

		this.statusIDV = sessionStatus.plaidResponse.status;
		return true;
	}

	/**
	 * @method save the customer; if it matches another customer; it'll update the customer.
	 * @param {string} storeCode  The storecode the customer is being saved from. IF ANY.
	 * @param {boolean} savedFromFIMenuDeal  Wether this customer was seved from a deal or customers page.
	 */
	async save(storeCode = null, savedFromFIMenuDeal = true, dealDate = null) {
		if (this.isEntity) this.dob = null;
		// We save the customer. The server will choose to either create it or change it.
		const response = await api.customers.createCustomerFromFIMenu(
			this,
			storeCode,
			savedFromFIMenuDeal ? ENUMS.CUSTOMER_CREATION_SOURCE.FIMenuDeal : ENUMS.CUSTOMER_CREATION_SOURCE.CustomersPage,
			dealDate,
		);

		// IF customer changed, it'll repopulate itself into the new customer.
		if (response?.data?.id != null && response?.data?.id != this.id) {
			this.id = response.data.id;
			this.setFromCustomer(response.data);
			this.loadCustomerData(response.data.id);

			LogRocket.track(`Cust_Created`, {
				customerId: this.id,
				customerCell: this.cell,
				customerEmail: this.email,
				customerDob: this.dob,
				storeCode: storeCode,
				environment: settings.environmentName,
			});
		}
	}

	/**
	 * This will fetch the customer and populate itself again.
	 *
	 * @param {boolean} [loadCreditReport=true]
	 * @memberof FIMenuCustomer
	 */
	async reload(loadCreditReport = true) {
		try {
			if (this.isEntity) this.dob = null;
			// We save the customer. The server will choose to either create it or change it.
			const response = await api.customers.getCustomerById(this.id);

			// IF customer changed, it'll repopulate itself into the new customer.
			if (response?.data) {
				this.setFromCustomer(response.data);
				if (loadCreditReport) await this.loadCustomerData(response.data.id);
			}
		} catch (err) {
			console.error('Error loading customer: ', err);
		}
	}

	async getCreditReport(dealId = null) {
		// Initialize response variable

		try {
			// Fetch credit report for a customer.
			const response = await api.creditreport.getByCustomerId(this.id, dealId);
			return new FIMenuCreditReport(response.data);
		} catch (error) {
			// Log any errors that occur during the process
			console.error('Error fetching credit report:', error);
		}

		return null;
	}

	/** Get the associated business customers if its a human customer. If its a business, get associated humans */
	async getAndSetAssociatedCustomers(isEntity) {
		try {
			let response;
			if (isEntity) {
				response = await api.customers.getAssociatedCustomersForBusiness(this.id);
			} else {
				response = await api.customers.getAssociatedBusinessesForCustomer(this.id);
			}

			if (response?.data?.statusCode == 200) {
				const customerRecordCustomers = response.data.payload;
				this.associatedCustomers = customerRecordCustomers.map(init => {
					var brandNewCustomer = new FIMenuCustomer();
					brandNewCustomer.setFromCustomer(init);
					return brandNewCustomer;
				});
			}

			if (response?.data.statusCode == 204) {
				LogRocket.log('No associated customers.');
				return;
			}
		} catch (error) {
			console.error('Error fetching associated customers.', error);
		}
	}

	//TODO: Rename to get and set.
	async getCreditPullsForCustomer() {
		let response = await CBCHelper.getCBCReportByCustomerId(this.id);
		const creditPullsData = response.data || [];
		const creditPullsArray = creditPullsData.map(init => new FIMenuCreditPulls(init));

		this.creditPulls = creditPullsArray;

		return creditPullsArray;
	}

	async loadCustomerData(dealID) {
		let creditReport = null;

		if (dealID) {
			creditReport = await this.getCreditReport(dealID);
		} else {
			creditReport = await this.getCreditReport();
		}

		this.creditReport = new FIMenuCreditReport(creditReport);
		this.getCreditPullsForCustomer(this.id);

		await this.getAndSetAssociatedCustomers(this.isEntity);
	}

	hasBasicRecordData() {
		const isCompany = util.isNullOrEmpty(this.dob) || this.isEntity;

		if (isCompany) return !!this.fullName;
		else return !!(this.dob && this.email && this.cell);
	}

	getPaperworkName() {
		if (this.isEntity && !util.isNullOrEmpty(this.fullName)) {
			return this.fullName;
		} else if (!util.isNullOrEmpty(this.firstName) || !util.isNullOrEmpty(this.lastName)) {
			return `${this.prefix ?? ''} ${this.firstName ?? ''} ${this.middleName ?? ''} ${this.lastName ?? ''} ${this.suffix ?? ''}`.replace('  ', ' ').trim();
		} else {
			return '';
		}
	}
}
